/**
 * Shared, page-local presentation helpers: Vietnamese labels for the shared
 * enums plus badge-colour mappings used by the tables across pages. Kept in the
 * pages slice so all page views render statuses and rule attributes consistently.
 */
import type { BadgeColor } from '@/components/common/Badge';
import type { SelectOption } from '@/components/Forms/Select';
import { ActionType, LogStatus, TargetType, TriggerType } from '@shared/constants';

/** Vietnamese label + badge colour for a Facebook entity status string. */
export function entityStatusMeta(status: string | null | undefined): {
  label: string;
  color: BadgeColor;
} {
  switch ((status ?? '').toUpperCase()) {
    case 'ACTIVE':
      return { label: 'Đang hoạt động', color: 'green' };
    case 'PAUSED':
      return { label: 'Tạm dừng', color: 'yellow' };
    case 'ARCHIVED':
      return { label: 'Đã lưu trữ', color: 'gray' };
    case 'DELETED':
      return { label: 'Đã xóa', color: 'red' };
    case 'DISABLED':
      return { label: 'Vô hiệu hóa', color: 'red' };
    default:
      return { label: status ?? '—', color: 'gray' };
  }
}

/** Vietnamese label + badge colour for an agent-log execution status. */
export function logStatusMeta(status: string | null | undefined): {
  label: string;
  color: BadgeColor;
} {
  switch ((status ?? '').toUpperCase()) {
    case LogStatus.SUCCESS:
      return { label: 'Thành công', color: 'green' };
    case LogStatus.FAILED:
      return { label: 'Thất bại', color: 'red' };
    case LogStatus.SKIPPED:
      return { label: 'Bỏ qua', color: 'gray' };
    case LogStatus.GUARDRAIL_BLOCKED:
      return { label: 'Bị chặn bởi guardrail', color: 'yellow' };
    default:
      return { label: status ?? '—', color: 'gray' };
  }
}

/** Human-readable Vietnamese labels for rule trigger types. */
export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  [TriggerType.REVENUE_THRESHOLD]: 'Ngưỡng doanh thu',
  [TriggerType.CPA_THRESHOLD]: 'Ngưỡng CPA',
  [TriggerType.ROAS_THRESHOLD]: 'Ngưỡng ROAS',
  [TriggerType.SPEND_THRESHOLD]: 'Ngưỡng chi tiêu',
};

/** Human-readable Vietnamese labels for rule action types. */
export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  [ActionType.INCREASE_BUDGET]: 'Tăng ngân sách',
  [ActionType.DECREASE_BUDGET]: 'Giảm ngân sách',
  [ActionType.PAUSE]: 'Tạm dừng',
  [ActionType.ACTIVATE]: 'Kích hoạt',
};

/** Human-readable Vietnamese labels for rule target types. */
export const TARGET_TYPE_LABELS: Record<TargetType, string> = {
  [TargetType.CAMPAIGN]: 'Chiến dịch',
  [TargetType.AD_SET]: 'Nhóm quảng cáo',
};

/** Select options derived from the trigger-type labels. */
export const TRIGGER_TYPE_OPTIONS: SelectOption[] = Object.entries(TRIGGER_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/** Select options derived from the action-type labels. */
export const ACTION_TYPE_OPTIONS: SelectOption[] = Object.entries(ACTION_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/** Select options derived from the target-type labels. */
export const TARGET_TYPE_OPTIONS: SelectOption[] = Object.entries(TARGET_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/** Whether these two action/trigger types deal with a percentage value. */
export function isPercentAction(actionType: ActionType): boolean {
  return actionType === ActionType.INCREASE_BUDGET || actionType === ActionType.DECREASE_BUDGET;
}
